// src/letterExports.js
// ─────────────────────────────────────────────────────────────
// The envelope-project exports → letters per day (10/11/26) — pure: no
// React, no network. Point-in-time cost per letter (src/supplyLots.js) reads
// the stored record; the screen (src/views/CostPerLetterLots.jsx, "Letter
// sources") reads the files.
//
// Reece, 10/10: "the only mail that would have gone out would have been
// envelopes that would have been on that sheet … there should not be any
// more mail … beyond what's listed there, aside from maybe less than 100
// handwritten letters." So inside an export's window (7/20 – 10/3) the two
// projects' exports ARE the letters; the CRM's mailing log is not.
//
//   Project A  "Export - Unique Households Mailed 75 Days <date>.csv"
//              (First Mailed, Batch, …, Letter Basis — "in project",
//              "handwritten-mailer made outside the project", "envelopes-only")
//              + "Export - Removed Rows 75 Days <date>.csv" (Reason Removed)
//   Project B  "lookback-75d-asof-20261003.csv" (date, batch, piece_type,
//              status, in_window_<mon><d>_<mon><d>) + its "-summary.csv"
//              (date, batch, piece_type, count, status — a cross-check)
//
// One letter per HOUSEHOLD per day: the same household on the same day in
// both projects (the FTR HVB B11–B14 envelopes printed in A, their letters
// in B) is one letter. A B "Letter only" row with no address is a letter on
// its day — unless project A has the envelopes of its batch, which ARE
// those letters (B11: 100, not 200). An A "envelopes-only" row that no
// letter collapses into still went out with a stamp: an envelope-only piece.
// "Not printed" rows never went out; rows printed after the window's end
// are waiting (a later export whose window covers them counts them).
// Dates are read by their leading YYYY-MM-DD — "2026-10-03 (approx)" is
// Oct 3 (678 such rows; a string compare dropped 487 October letters).
//
// The record kept (cost_per_letter_config "letterSources") holds counts per
// day and the left-out counts ONLY — never a name, address or batch row.
// ─────────────────────────────────────────────────────────────

var DEFAULT_WINDOW_DAYS=75;
var MONTHS3=["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
var REMOVED_SAME_RE=/^\s*same household already counted in\b.*/i;

function pad2(n){return(n<10?"0":"")+n;}
function oneLine(s){return String(s==null?"":s).replace(/\s+/g," ").trim();}
function dayNum(day){var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(day||"");return m?Date.UTC(+m[1],+m[2]-1,+m[3])/864e5:NaN;}
function dayFromNum(n){var d=new Date(n*864e5);return d.getUTCFullYear()+"-"+pad2(d.getUTCMonth()+1)+"-"+pad2(d.getUTCDate());}
function addDays(day,n){var t=dayNum(day);return isNaN(t)?null:dayFromNum(t+n);}
function isDay(s){var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s||""));if(!m)return false;var t=dayNum(s);return!isNaN(t)&&dayFromNum(t)===s;}

// CSV text → rows of cells (a BOM, quoted cells with commas / quotes /
// newlines, CRLF). → {header: [names, trimmed], rows: [{name: cell}]}.
function parseCsvText(text){
  var t=String(text==null?"":text);
  if(t.charCodeAt(0)===0xfeff)t=t.slice(1);
  var rows=[];var r=[];var f="";var q=false;
  for(var i=0;i<t.length;i++){
    var c=t[i];
    if(q){if(c==='"'){if(t[i+1]==='"'){f+='"';i++;}else q=false;}else f+=c;}
    else if(c==='"')q=true;
    else if(c===","){r.push(f);f="";}
    else if(c==="\n"){r.push(f.replace(/\r$/,""));rows.push(r);r=[];f="";}
    else f+=c;
  }
  if(f||r.length){r.push(f.replace(/\r$/,""));rows.push(r);}
  var header=(rows.shift()||[]).map(function(s){return String(s).trim();});
  var out=rows.filter(function(x){return x.length>1||(x.length===1&&String(x[0]).trim());}).map(function(x){var o={};header.forEach(function(k,j){o[k]=String(x[j]==null?"":x[j]).trim();});return o;});
  return{header:header,rows:out};
}
function hasCol(header,name){var n=String(name).toLowerCase();return(header||[]).some(function(h){return String(h).trim().toLowerCase()===n;});}
function col(row,name){
  if(!row)return"";
  if(row[name]!=null)return row[name];
  var n=name.toLowerCase();
  for(var k in row)if(k.toLowerCase()===n)return row[k];
  return"";
}
// Which export a file is, by its HEADERS: A (households mailed), A_REMOVED
// (its removed rows), B (the lookback rows), B_SUMMARY (its counts) — else
// null ("not an envelope-project export").
function exportKind(header,name){
  if(hasCol(header,"First Mailed")&&hasCol(header,"Letter Basis"))return"A";
  if(hasCol(header,"Reason Removed"))return"A_REMOVED";
  if(hasCol(header,"piece_type")&&hasCol(header,"status")&&hasCol(header,"count"))return"B_SUMMARY";
  if(hasCol(header,"piece_type")&&hasCol(header,"status"))return"B";
  return null;
}
// The day an export cell names: its leading YYYY-MM-DD — "2026-10-03
// (approx)" is Oct 3. Never compared as a raw string.
function exportDay(s){
  var m=/^\s*(\d{4})-(\d{2})-(\d{2})/.exec(String(s==null?"":s));
  if(!m)return null;
  var d=m[1]+"-"+m[2]+"-"+m[3];
  return isDay(d)?d:null;
}
function isApprox(s){return/approx|~|\best\b/i.test(String(s||""));}
// A household: the street address, lower case, "#" → unit, punctuation out,
// the usual suffixes shortened (street → st), + the 5-digit zip. In memory
// only — never stored. Empty address → null.
var SUFFIX={street:"st",st:"st",avenue:"ave",ave:"ave",av:"ave",road:"rd",rd:"rd",drive:"dr",dr:"dr",lane:"ln",ln:"ln",court:"ct",ct:"ct",circle:"cir",cir:"cir",boulevard:"blvd",blvd:"blvd",place:"pl",pl:"pl",terrace:"ter",ter:"ter",way:"way",parkway:"pkwy",pkwy:"pkwy",highway:"hwy",hwy:"hwy",trail:"trl",trl:"trl",north:"n",south:"s",east:"e",west:"w",n:"n",s:"s",e:"e",w:"w",apartment:"unit",apt:"unit",unit:"unit",suite:"unit",ste:"unit"};
function householdKey(address,zip){
  var s=String(address==null?"":address).toLowerCase().replace(/#/g," unit ").replace(/[^a-z0-9 ]/g," ").split(/\s+/).filter(Boolean).map(function(x){return SUFFIX[x]||x;}).join(" ");
  return s?s+"|"+String(zip==null?"":zip).replace(/\D/g,"").slice(0,5):null;
}
function monIndex(w){var i=MONTHS3.indexOf(String(w||"").slice(0,3).toLowerCase());return i<0?null:i+1;}
// The window the files cover → {from, to, asOf, days, from_: how}. asOf =
// "asof-YYYYMMDD" in a file name, else opts.asOf, else the latest B Printed
// day whose in-window flag is "Yes"; days = "NN Days" / "NNd" in a name
// (default 75); from = asOf − days. A B column "in_window_<mon><d>_<mon><d>"
// names both ends (asOf's year) and wins.
function exportWindow(files,opts){
  opts=opts||{};
  var asOf=null,days=null,inCol=null,lastYes=null;
  (files||[]).forEach(function(f){
    var nm=String(f.name||"");
    var a=/asof[-_ ]?(\d{4})(\d{2})(\d{2})/i.exec(nm);if(a&&!asOf){var d=a[1]+"-"+a[2]+"-"+a[3];if(isDay(d))asOf=d;}
    var n=/(\d{1,3})\s*days?\b/i.exec(nm)||/(\d{1,3})d\b/i.exec(nm);if(n&&days==null)days=+n[1];
    (f.header||[]).forEach(function(h){if(/^in_window_[a-z]{3}\d{1,2}_[a-z]{3}\d{1,2}$/i.test(h))inCol=h;});
    if(f.kind==="B")(f.rows||[]).forEach(function(r){
      if(col(r,"status")!=="Printed")return;
      var flag=null;for(var k in r)if(/^in_window_/i.test(k))flag=r[k];
      if(flag!=null&&flag!=="Yes")return;
      var d=exportDay(col(r,"date"));if(d&&(!lastYes||d>lastYes))lastYes=d;
    });
  });
  if(!asOf&&isDay(opts.asOf))asOf=opts.asOf;
  if(!asOf)asOf=lastYes;
  if(days==null)days=opts.windowDays||DEFAULT_WINDOW_DAYS;
  if(!asOf)return null;
  var w={from:addDays(asOf,-days),to:asOf,asOf:asOf,days:days,how:"asOf − "+days+" days"};
  if(inCol){
    var m=/^in_window_([a-z]{3})(\d{1,2})_([a-z]{3})(\d{1,2})$/i.exec(inCol);
    var y=asOf.slice(0,4);
    var a1=monIndex(m[1]),a2=monIndex(m[3]);
    if(a1&&a2){
      var f1=y+"-"+pad2(a1)+"-"+pad2(+m[2]),t1=y+"-"+pad2(a2)+"-"+pad2(+m[4]);
      if(isDay(f1)&&isDay(t1)&&f1<=t1){w.from=f1;w.to=t1;w.how="the export's “"+inCol+"” column";w.agrees=f1===addDays(asOf,-days)&&t1===asOf;}
    }
  }
  return w;
}
function hashStr(s){var h=5381;s=String(s);for(var i=0;i<s.length;i++)h=((h<<5)+h+s.charCodeAt(i))|0;return(h>>>0).toString(36);}
function bump(o,k,n){o[k]=(o[k]||0)+(n==null?1:n);}
function dayRec(days,d){var x=days[d];if(!x){x={p:0,hw:0,e:0,w:0};days[d]=x;}return x;}

// The export files ([{name, text}]) → the stored letter-source record
// (aggregates only): {id, importedAt, asOf, window, files, days: {day: {p
// (letters, incl. hw), hw (handwritten-mailer letters), e (envelope-only
// pieces), w (printed after the window — waiting; we = the envelope-only
// pieces among them, only when there are any)}}, leftOut, summaryCheck,
// totals: {letters, envelopeOnly, byMonth}} — or {error} when no file is an
// export or no window can be told.
function ledgerFromExports(files,opts){
  opts=opts||{};
  var parsed=(files||[]).map(function(f){
    var p=parseCsvText(f.text);
    return{name:String(f.name||""),size:String(f.text||"").length,header:p.header,rows:p.rows,kind:exportKind(p.header,f.name)};
  });
  var used=parsed.filter(function(f){return f.kind;});
  var notExports=parsed.filter(function(f){return!f.kind;}).map(function(f){return f.name;});
  if(!used.length)return{error:"None of these is an envelope-project export (the households-mailed export, its removed rows, or the lookback rows / summary).",notExports:notExports};
  var win=exportWindow(used,opts);
  if(!win)return{error:"Couldn't tell which days these exports cover — the file names carry no “asof-YYYYMMDD” and no row is marked printed in the window.",notExports:notExports};
  var leftOut={notPrinted:0,afterCutoff:{letters:0,envelopes:0},outside:0,collapsed:0,absorbed:0,letterOnlyCounted:0,approx:0,noDate:0,removed:{}};
  var days={};
  var cands=[];
  var aEnvBatches={};
  used.filter(function(f){return f.kind==="A";}).forEach(function(f){
    f.rows.forEach(function(r){if(/envelopes?[- ]only/i.test(col(r,"Letter Basis")))aEnvBatches[oneLine(col(r,"Batch")).toLowerCase()]=true;});
  });
  // 1. Project B rows
  used.filter(function(f){return f.kind==="B";}).forEach(function(f){
    f.rows.forEach(function(r){
      var raw=col(r,"date");
      if(col(r,"status")!=="Printed"){leftOut.notPrinted++;return;}
      var day=exportDay(raw);
      if(!day){leftOut.noDate++;return;}
      if(isApprox(raw))leftOut.approx++;
      var flag=null;for(var k in r)if(/^in_window_/i.test(k))flag=r[k];
      var letter=/letter/i.test(col(r,"piece_type"));
      if(day>win.to||flag==="After cutoff"){var dw=dayRec(days,day);dw.w++;if(letter)leftOut.afterCutoff.letters++;else{leftOut.afterCutoff.envelopes++;dw.we=(dw.we||0)+1;}return;}
      cands.push({day:day,key:householdKey(col(r,"address"),col(r,"zip")),batch:oneLine(col(r,"batch")).toLowerCase(),letter:letter,hw:false});
    });
  });
  // 2. Project A rows
  used.filter(function(f){return f.kind==="A";}).forEach(function(f){
    f.rows.forEach(function(r){
      var raw=col(r,"First Mailed");var day=exportDay(raw);
      if(!day){leftOut.noDate++;return;}
      if(isApprox(raw))leftOut.approx++;
      var basis=col(r,"Letter Basis");
      cands.push({day:day,key:householdKey(col(r,"Address"),col(r,"Zip")),batch:oneLine(col(r,"Batch")).toLowerCase(),letter:!/envelopes?[- ]only/i.test(basis),hw:/handwritten/i.test(basis)});
    });
  });
  // 3. Inside the window only.
  cands=cands.filter(function(x){if(x.day<win.from||x.day>win.to){leftOut.outside++;return false;}return true;});
  // 4. Address-less rows first (their batches decide the A envelopes they are).
  var absorbedBatches={};
  cands.filter(function(x){return!x.key;}).forEach(function(x){
    if(aEnvBatches[x.batch]){leftOut.absorbed++;absorbedBatches[x.batch]=true;return;}
    leftOut.letterOnlyCounted++;
    dayRec(days,x.day).p++;
  });
  // 5. One per household per day (the first wins; letter / handwritten OR-ed).
  var seen=new Map();
  cands.filter(function(x){return!!x.key;}).forEach(function(x){
    var k=x.key+"|"+x.day;var p=seen.get(k);
    if(p){leftOut.collapsed++;p.letter=p.letter||x.letter;p.hw=p.hw||x.hw;return;}
    seen.set(k,{day:x.day,batch:x.batch,letter:x.letter,hw:x.hw});
  });
  // 6. Each unique one.
  seen.forEach(function(x){
    var d=dayRec(days,x.day);
    if(x.letter){d.p++;if(x.hw)d.hw++;return;}
    if(absorbedBatches[x.batch]){d.p++;return;}   // its letter is the batch's address-less B row
    d.e++;
  });
  // 7. The removed rows' reasons (another batch's name never kept).
  used.filter(function(f){return f.kind==="A_REMOVED";}).forEach(function(f){
    f.rows.forEach(function(r){
      var why=oneLine(col(r,"Reason Removed"));
      if(REMOVED_SAME_RE.test(why))why="Same household already counted in another batch";
      bump(leftOut.removed,why||"(no reason given)");
    });
  });
  // 8. The summary's counts vs the rows.
  var summaryCheck={ok:true,mismatches:[],checked:false};
  var sums=used.filter(function(f){return f.kind==="B_SUMMARY";});
  if(sums.length){
    summaryCheck.checked=true;
    var have={};
    used.filter(function(f){return f.kind==="B";}).forEach(function(f){f.rows.forEach(function(r){bump(have,[exportDay(col(r,"date"))||"",oneLine(col(r,"batch")).toLowerCase(),oneLine(col(r,"piece_type")).toLowerCase(),oneLine(col(r,"status")).toLowerCase()].join("|"));});});
    var bRows=used.some(function(f){return f.kind==="B";});
    sums.forEach(function(f){f.rows.forEach(function(r){
      var k=[exportDay(col(r,"date"))||"",oneLine(col(r,"batch")).toLowerCase(),oneLine(col(r,"piece_type")).toLowerCase(),oneLine(col(r,"status")).toLowerCase()].join("|");
      var want=parseInt(String(col(r,"count")).replace(/,/g,""),10)||0;
      if(bRows&&(have[k]||0)!==want){summaryCheck.ok=false;if(summaryCheck.mismatches.length<20)summaryCheck.mismatches.push({day:exportDay(col(r,"date")),piece:oneLine(col(r,"piece_type")),status:oneLine(col(r,"status")),summary:want,rows:have[k]||0});}
    });});
    if(!bRows)summaryCheck.ok=null;
  }
  // 9. Totals.
  var byMonth={};var letters=0,env=0,waiting=0,hw=0;
  Object.keys(days).sort().forEach(function(d){var x=days[d];if(x.p||x.e){bump(byMonth,d.slice(0,7),x.p+x.e);}letters+=x.p;env+=x.e;waiting+=x.w;hw+=x.hw;});
  var importedAt=opts.importedAt||new Date().toISOString();
  var sig=used.map(function(f){return f.name+":"+f.size;}).sort().join("|");
  return{
    id:"LS-"+win.asOf+"-"+hashStr(sig),
    importedAt:importedAt,asOf:win.asOf,window:{from:win.from,to:win.to},windowHow:win.how,
    files:used.map(function(f){return{name:f.name,kind:f.kind,rows:f.rows.length};}),
    notExports:notExports,
    days:days,leftOut:leftOut,summaryCheck:summaryCheck,
    // letters = every stamped piece (an envelope-only piece went out with a stamp too)
    totals:{letters:letters+env,printed:letters,envelopeOnly:env,handwrittenMailer:hw,waiting:waiting,byMonth:byMonth}
  };
}

export { DEFAULT_WINDOW_DAYS, parseCsvText, exportKind, exportDay, householdKey, exportWindow, ledgerFromExports };
